import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { CaseCptLine } from '@/lib/templates/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logCptEdit } from '@/lib/audit/logCptEdit';

interface RemoveProcedureDialogProps {
  line: CaseCptLine | null;
  onClose: () => void;
  onRemoveCaseOnly: (code: string) => void;
  onTemplateUpdated?: () => void;
  templateId?: string;
  templateName?: string;
  caseId?: string;
}

export function RemoveProcedureDialog({
  line,
  onClose,
  onRemoveCaseOnly,
  onTemplateUpdated,
  templateId,
  templateName,
  caseId,
}: RemoveProcedureDialogProps) {
  const [confirmingTemplate, setConfirmingTemplate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  if (!line) return null;

  const handleEditTemplate = async () => {
    if (!confirmingTemplate) {
      setConfirmingTemplate(true);
      return;
    }

    if (!templateId) {
      toast.error('No template linked to this case');
      return;
    }

    setIsSaving(true);
    try {
      // Fetch current template
      const { data: template, error: fetchErr } = await supabase
        .from('surgeon_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (fetchErr || !template) throw new Error('Failed to load template');

      const currentVersion = template.version ?? 1;
      const newVersion = currentVersion + 1;

      // Remove the CPT from the appropriate list
      const primaryCpts = Array.isArray(template.primary_cpts) ? template.primary_cpts : [];
      const secondaryCpts = Array.isArray(template.secondary_cpts) ? template.secondary_cpts : [];

      const newPrimary = primaryCpts.filter((c: any) => c.code !== line.code);
      const newSecondary = secondaryCpts.filter((c: any) => c.code !== line.code);

      // Save version snapshot before updating
      await supabase.from('surgeon_template_versions').insert({
        template_id: templateId,
        version: newVersion,
        primary_cpts: newPrimary,
        secondary_cpts: newSecondary,
        narrative_template: template.narrative_template,
        common_addons: template.common_addons,
        change_description: `Removed ${line.code} (${line.description}) from template`,
        changed_by: 'surgeon',
      });

      // Update the master template
      const { error: updateErr } = await supabase
        .from('surgeon_templates')
        .update({
          primary_cpts: newPrimary,
          secondary_cpts: newSecondary,
          version: newVersion,
          updated_at: new Date().toISOString(),
        })
        .eq('id', templateId);

      if (updateErr) throw updateErr;

      // Also remove from current case
      onRemoveCaseOnly(line.code);
      onTemplateUpdated?.();

      // Audit log
      if (caseId) {
        logCptEdit({
          caseId,
          action: 'remove_procedure',
          cptCode: line.code,
          section: line.section,
          scope: 'master_template',
          templateId,
          templateVersionBefore: currentVersion,
          templateVersionAfter: newVersion,
          details: { description: line.description } as any,
        });
      }

      toast.success(`Template updated to v${newVersion} — ${line.code} removed permanently`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update template');
    } finally {
      setIsSaving(false);
      setConfirmingTemplate(false);
      onClose();
    }
  };

  const handleClose = () => {
    setConfirmingTemplate(false);
    onClose();
  };

  return (
    <Dialog open={!!line} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Remove Procedure</DialogTitle>
          <DialogDescription>
            Remove <strong>{line.code}</strong> — {line.description}
          </DialogDescription>
        </DialogHeader>

        {confirmingTemplate ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-destructive">Permanent Template Change</p>
                <p className="text-muted-foreground mt-1">
                  This will remove <strong>{line.code}</strong> from the master template
                  "{templateName}" and create a new version. This affects all future cases using this template.
                </p>
              </div>
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button
                variant="destructive"
                onClick={handleEditTemplate}
                disabled={isSaving}
                className="w-full"
              >
                {isSaving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Confirm — Update Template
              </Button>
              <Button variant="ghost" onClick={handleClose} className="w-full">
                Cancel
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              Remove from this case only, or edit the master template?
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button onClick={() => {
                onRemoveCaseOnly(line.code);
                if (caseId) {
                  logCptEdit({
                    caseId,
                    action: 'remove_procedure',
                    cptCode: line.code,
                    section: line.section,
                    scope: 'case_only',
                    templateId,
                    details: { description: line.description } as any,
                  });
                }
                handleClose();
              }} className="w-full">
                Remove for this case only
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleEditTemplate}
                disabled={!templateId}
              >
                Edit master template ({templateName || 'Template'})
              </Button>
              <Button variant="ghost" onClick={handleClose} className="w-full">
                Cancel
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
